import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { AtSign, Bell, CheckCircle2, RotateCcw, Trash2, UserRoundPlus } from 'lucide-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../ui/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tabs, TabsList, TabsTrigger } from '../UI/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../UI/alert-dialog';
import {
  NotificationFilter,
  NotificationItem,
  deleteNotification,
  getNotifications,
  getUnreadNotificationsCount,
  markNotificationsRead,
  resolveAllMentionNotifications,
  resolveNotification,
} from '../../api/notifications';
import { toast } from 'sonner';
import { Button } from "../ui/button";

const MOBILE_BREAKPOINT = 767;
const NOTIFICATIONS_PAGE_SIZE = 50;

const formatUnreadCount = (count: number) => {
  if (count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
};

const isMentionType = (type: string) => type === 'mention' || type === 'mention_resolved';
const isAssignmentType = (type: string) => type === 'project_assigned' || type === 'item_assigned';
const isMentionResolvedType = (type: string) => type === 'mention_resolved';

const getNotificationTypeLabel = (type: string) => {
  if (isMentionType(type)) return 'Mention';
  if (isAssignmentType(type)) return 'Assignment';
  return 'Notification';
};

const getNotificationIcon = (type: string) => {
  if (isMentionResolvedType(type)) {
    return {
      icon: <AtSign className="h-4 w-4 text-[#14A44D]" />,
      bgClass: 'bg-[#CFF3DE]',
    };
  }

  if (type === 'mention') {
    return {
      icon: <AtSign className="h-4 w-4 text-[#2E6FDB]" />,
      bgClass: 'bg-[#DDEAFB]',
    };
  }

  return {
    icon: <UserRoundPlus className="h-4 w-4 text-[#A87800]" />,
    bgClass: 'bg-[#F3E7B0]',
  };
};

const getUserDisplayName = (
  user: NotificationItem['originator'] | NotificationItem['resolvedBy']
) => {
  if (!user) return 'A teammate';

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  if (user.label) return `@${user.label}`;

  return 'A teammate';
};

const getMentionTitle = (item: NotificationItem) => {
  if (!isMentionResolvedType(item.type)) return item.title;

  const resolverName = getUserDisplayName(item.originator || item.resolvedBy);
  return `${resolverName} resolved your mention`;
};

const getMentionMessage = (item: NotificationItem) => {
  const rawMessage = String(item.message || '').trim();
  if (!rawMessage) return '';

  if (isMentionResolvedType(item.type)) {
    const resolverName = getUserDisplayName(item.originator || item.resolvedBy);
    const locationMatch = rawMessage.match(/\bon\s+(.+)$/i);
    if (locationMatch?.[1]) {
      return `${resolverName} resolved the mention you left for them on ${locationMatch[1]}`;
    }
    return rawMessage;
  }

  if (item.type === 'mention') {
    return /^from\s/i.test(rawMessage) ? rawMessage : `from ${rawMessage}`;
  }

  return rawMessage;
};

const renderAssignmentMessage = (message: string) => {
  const normalizedMessage = String(message || '').trim();
  if (!normalizedMessage) return null;

  const match = normalizedMessage.match(/^(You were assigned to )(.+?)( as .+)$/i);
  if (!match) {
    return <span className="text-[#607188]">{normalizedMessage}</span>;
  }

  const [, prefix, locationPart, suffix] = match;
  return (
    <>
      <span className="text-[#607188]">{prefix}</span>
      <span className="text-[#2F8FB0]">{locationPart}</span>
      <span className="text-[#607188]">{suffix}</span>
    </>
  );
};

function buildNavigationPath(notification: NotificationItem) {
  const metadata = notification.metadata || {};
  const baseRoute =
    metadata.route ||
    (metadata.clientId
      ? `/clients/${metadata.clientId}`
      : notification.clientId
        ? `/clients/${notification.clientId}`
        : '/projects');

  const params = new URLSearchParams();
  if (isMentionType(notification.type)) {
    params.set('openNotebook', 'true');
    if (metadata.table) params.set('notebookTable', String(metadata.table));
    if (metadata.tabType) params.set('tabType', String(metadata.tabType));
    if (metadata.componentId) params.set('componentId', String(metadata.componentId));
    if (metadata.componentIdType) params.set('componentIdType', String(metadata.componentIdType));
    if (metadata.componentKey) params.set('componentKey', String(metadata.componentKey));
    if (metadata.tableType) params.set('tableType', String(metadata.tableType));
    if (metadata.noteId || notification.notebookId) {
      params.set('noteId', String(metadata.noteId || notification.notebookId));
    }
    if (metadata.projectId || notification.projectId) {
      params.set('projectId', String(metadata.projectId || notification.projectId));
    }
    if (metadata.itemId || metadata.orderId || notification.orderId) {
      params.set('itemId', String(metadata.itemId || metadata.orderId || notification.orderId));
    }
  } else if (notification.projectId) {
    params.set('projectId', String(notification.projectId));
  }

  const query = params.toString();
  return query ? `${baseRoute}?${query}` : baseRoute;
}

type NotificationsMenuProps = RouteComponentProps;

const NotificationsMenuBase = ({ history }: NotificationsMenuProps) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= MOBILE_BREAKPOINT);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<NotificationFilter>('all');
  const [hideResolvedMentions, setHideResolvedMentions] = useState<boolean>(true);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [mentionUnresolvedCount, setMentionUnresolvedCount] = useState<number>(0);
  const [mentionResolvedCount, setMentionResolvedCount] = useState<number>(0);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadNotificationsCount();
      setUnreadCount(Number(res?.unreadCount || 0));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadNotifications = useCallback(
    async (options?: { append?: boolean; offset?: number }) => {
      const append = Boolean(options?.append);
      const offset = Math.max(0, Number(options?.offset) || 0);

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const res = await getNotifications({
          filter: activeTab,
          hideResolvedMentions,
          limit: NOTIFICATIONS_PAGE_SIZE,
          offset,
        });
        const nextItems = Array.isArray(res?.items) ? res.items : [];
        setItems((prevItems) => {
          if (!append) return nextItems;

          // Preserve paging order while deduplicating by id.
          const mergedById = new Map<number, NotificationItem>();
          [...prevItems, ...nextItems].forEach((item) => {
            if (!item?.id) return;
            mergedById.set(item.id, item);
          });
          return Array.from(mergedById.values());
        });
        setTotalItems(Number(res?.total || 0));
        setUnreadCount(Number(res?.unreadCount || 0));
        setMentionUnresolvedCount(Number(res?.mentionUnresolvedCount || 0));
        setMentionResolvedCount(Number(res?.mentionResolvedCount || 0));
      } catch (error) {
        console.error(error);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [activeTab, hideResolvedMentions]
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadUnreadCount();
    const intervalId = window.setInterval(loadUnreadCount, 30000);
    return () => window.clearInterval(intervalId);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!open) return;
    loadNotifications();
  }, [open, loadNotifications]);

  useEffect(() => {
    if (!open) return;
    const intervalId = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(intervalId);
  }, [open, loadNotifications]);

  const hasUnresolvedMentions = useMemo(() => mentionUnresolvedCount > 0, [mentionUnresolvedCount]);
  const hasMentionItems = useMemo(
    () => mentionUnresolvedCount + mentionResolvedCount > 0,
    [mentionResolvedCount, mentionUnresolvedCount]
  );
  const hasMoreItems = useMemo(() => items.length < totalItems, [items.length, totalItems]);

  const handleToggleOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  const handleDeleteNotification = async (id: number) => {
    setActionLoadingId(id);
    try {
      await deleteNotification(id);
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResolveNotification = async (item: NotificationItem, resolved: boolean) => {
    if (!item?.id) return;
    setActionLoadingId(item.id);
    try {
      await resolveNotification({
        id: item.id,
        resolved,
      });
      toast(resolved ? 'Notification marked as resolved' : 'Notification unresolved');
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleBulkResolveMentions = async () => {
    if (!hasMentionItems) return;
    const nextResolved = hasUnresolvedMentions;
    setActionLoadingId(-1);
    try {
      await resolveAllMentionNotifications({
        resolved: nextResolved,
      });
      toast(
        nextResolved ? 'All mention notifications resolved' : 'All mention notifications unresolved'
      );
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenNotification = async (item: NotificationItem) => {
    if (!item?.id) return;

    const markReadIds = item.isRead ? [] : [item.id];
    if (markReadIds.length > 0) {
      try {
        await markNotificationsRead({ ids: markReadIds });
      } catch (error) {
        console.error(error);
      }
    }

    const path = buildNavigationPath(item);
    setOpen(false);
    history.push(path);
    loadUnreadCount();
  };

  const handleLoadMore = async () => {
    if (loading || loadingMore || !hasMoreItems) return;
    await loadNotifications({
      append: true,
      offset: items.length,
    });
  };

  const renderFooter = () => {
    if (activeTab === 'assignments') return null;
    const bulkLabel = hasUnresolvedMentions ? 'Mark all resolved' : 'Unresolve all';

    return (
      <div className="flex items-center justify-between border-t border-border/30 py-[8px] px-[16px] leading-normal bg-muted/50">
        <button
          type="button"
          onClick={handleBulkResolveMentions}
          disabled={actionLoadingId === -1 || !hasMentionItems}
          className="text-[11px] font-semibold text-[#5B708D] hover:text-[#42566F] disabled:opacity-50"
        >
          {bulkLabel}
        </button>

        <button
          type="button"
          onClick={() => setHideResolvedMentions((prev) => !prev)}
          data-resolved={hideResolvedMentions ? false : true}
          className="rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors cursor-pointer border border-green-600/40 data-[resolved=true]:border-transparent text-green-600 data-[resolved=true]:bg-green-100 data-[resolved=true]:hover:bg-green-200 data-[resolved=false]:hover:bg-green-50"
        >
          {hideResolvedMentions ? 'Hiding resolved' : 'Showing resolved'}
        </button>
      </div>
    );
  };

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          Loading notifications...
        </div>
      );
    }

    if (!items.length) {
      return (
        <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          No notifications found
        </div>
      );
    }

    return (
      <div
        className={`${isMobile ? 'max-h-[calc(100vh-220px)]' : 'max-h-[280px]'} overflow-y-auto`}
      >
        {items.map((item) => {
          const icon = getNotificationIcon(item.type);
          const itemTypeLabel = getNotificationTypeLabel(item.type);
          const showResolve = isMentionType(item.type);
          const isActionLoading = actionLoadingId === item.id;
          const relativeTime = item.createdAt ? moment(item.createdAt).fromNow() : '';
          const itemTitle = isMentionType(item.type) ? getMentionTitle(item) : item.title;
          const itemMessage = isMentionType(item.type) ? getMentionMessage(item) : item.message;
          const showResolvedBadge = item.isResolved && !isMentionResolvedType(item.type);
          const messageContent = isAssignmentType(item.type)
            ? renderAssignmentMessage(String(itemMessage || ''))
            : itemMessage;

          return (
            <div
              key={item.id}
              className="group relative cursor-pointer border-b border-border/60 bg-white px-[16px] py-[12px] transition-colors hover:bg-muted/20"
              onClick={() => handleOpenNotification(item)}
            >
              {!item.isRead && (
                <span className="absolute left-2 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-red-500" />
              )}

              <div className="flex items-start gap-3 pl-3">
                <span
                  className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${icon.bgClass}`}
                >
                  {icon.icon}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 pr-16 text-left text-[14px] font-semibold leading-4 text-foreground">
                    {itemTitle}
                  </div>
                  {messageContent && (
                    <div className="mt-1 line-clamp-3 text-left text-[12px] text-muted-foreground">
                      {messageContent}
                    </div>
                  )}

                  <div className="mt-1 flex items-center gap-2 text-[10px] leading-normal text-muted-foreground">
                    <span>{relativeTime}</span>
                    {/* <span className="rounded bg-muted px-2 py-0.5 text-[#607188]"> */}
                    <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {itemTypeLabel}
                    </span>
                    {showResolvedBadge && (
                      <span className="flex items-center gap-1 text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">
                        <CheckCircle2 className="h-3 w-3" />
                        Resolved
                      </span>
                    )}
                  </div>

                  {showResolve && (
                    <button
                      type="button"
                      className="ml-auto block text-[11px] font-semibold text-[#2F8FB0] hover:text-[#267990]"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenNotification(item);
                      }}
                    >
                      View mention
                    </button>
                  )}
                </div>

                <div className="absolute right-4 top-4 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  {showResolve && (
                    <button
                      type="button"
                      title={item.isResolved ? 'Unresolve' : 'Mark as resolved'}
                      disabled={isActionLoading}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleResolveNotification(item, !item.isResolved);
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-[#5E6C84] hover:bg-[#E9EEF5] hover:text-[#374151] disabled:opacity-50"
                    >
                      {item.isResolved ? (
                        <RotateCcw className="h-4 w-4" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                    </button>
                  )}

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant={'trash-button'}
                        type="button"
                        title="Delete notification"
                        disabled={isActionLoading}
                        onClick={(event) => event.stopPropagation()}
                        // className="inline-flex h-7 w-7 items-center justify-center rounded text-[#5E6C84] hover:bg-[#FDEBEC] hover:text-red-600 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent
                      className="md:min-w-[520px]"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <AlertDialogHeader className="gap-0">
                        <AlertDialogTitle className="m-0 text-lg font-semibold">
                          Delete notification
                        </AlertDialogTitle>
                        <AlertDialogDescription className="mt-2 text-sm text-muted-foreground">
                          Are you sure you want to delete this notification?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          variant="delete"
                          onClick={() => handleDeleteNotification(item.id)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          );
        })}
        {hasMoreItems && (
          <div className="flex justify-center border-t border-border/50 px-4 py-3">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="text-[11px] font-semibold text-[#2F8FB0] hover:text-[#267990] disabled:opacity-60"
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const panel = (
    <div className="w-full bg-background">
      <div className="border-b border-border/60">
        <div
          className={`flex ${isMobile ? 'flex-col items-start' : 'items-center justify-between'} gap-2 px-4 py-3 border-b border-input bg-muted/50`}
          // className={`flex ${
          //   isMobile ? 'flex-col items-start gap-2' : 'items-center justify-between gap-2'
          // }`}
        >
          {/* <div className="shrink-0 text-left text-[13px] font-semibold leading-none text-foreground"> */}
          <h3 className="font-semibold text-[16px] m-0">Notifications</h3>
          <Tabs
            // className={isMobile ? 'w-full' : 'shrink-0'}
            className="flex gap-1"
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as NotificationFilter)}
          >
            <TabsList
              className={
                isMobile
                  ? 'grid h-8 w-full grid-cols-3 bg-transparent p-0'
                  : 'inline-flex group-data-[orientation=horizontal]/tabs:h-[28px] w-auto items-center gap-1 bg-transparent p-0'
              }
            >
              <TabsTrigger
                value="all"
                className="!flex-none h-8 text-[11px] font-semibold data-[state=active]:bg-secondary data-[state=active]:shadow-none py-0 px-[8px]"
              >
                All
              </TabsTrigger>
              <TabsTrigger
                value="mentions"
                className="!flex-none h-8 text-[11px] font-semibold data-[state=active]:bg-secondary data-[state=active]:shadow-none py-0 px-[8px]"
              >
                Mentions
              </TabsTrigger>
              <TabsTrigger
                value="assignments"
                className="!flex-none h-8 text-[11px] font-semibold data-[state=active]:bg-secondary data-[state=active]:shadow-none py-0 px-[8px]"
              >
                Assignments
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {renderList()}
      {renderFooter()}
    </div>
  );

  const bellButton = (onClick?: () => void) => (
    <button
      type="button"
      className="relative border border-white rounded-full inline-flex h-9 w-9 items-center justify-center text-white transition-colors hover:bg-white group"
      onClick={onClick}
      aria-label="Open notifications"
    >
      <Bell className="h-[17px] w-[17px] fill-transparent group-hover:stroke-primary group-hover:fill-primary" />
      {unreadCount > 0 && (
        <span className="absolute -right-1 -top-1 inline-flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold leading-[1.2] text-white">
          {formatUnreadCount(unreadCount)}
        </span>
      )}
    </button>
  );

  if (isMobile) {
    return (
      <>
        {bellButton(() => setOpen((prev) => !prev))}
        <Drawer open={open} onOpenChange={handleToggleOpen}>
          <DrawerContent className="max-h-[95vh]">
            <DrawerHeader className="hidden">
              <DrawerTitle>Notifications</DrawerTitle>
            </DrawerHeader>
            {panel}
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleToggleOpen}>
      <PopoverTrigger asChild>{bellButton()}</PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="w-[384px] p-0">
        {panel}
      </PopoverContent>
    </Popover>
  );
};

export const NotificationsMenu: React.ComponentType = withRouter(NotificationsMenuBase);

export default NotificationsMenu;
